import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu as MenuIcon, Download, Calendar, ChevronDown, DollarSign, ShoppingBag, CalendarCheck, Repeat, Percent, XCircle, Crown, Cake, TrendingUp, TrendingDown, Users, Activity, MapPin, Landmark, CheckCircle2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { Sidebar } from './Sidebar';
import { useBranches } from './BranchesContext';
import { useCRM, getCustomerMetrics, tagMeta } from './CRMContext';
import { useOrders, methodConfig } from './OrdersContext';
type TimeRange = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom';
const rand = (seed: number) => {
  const x = Math.sin(seed * 9.7) * 10000;
  return x - Math.floor(x);
};
function generateTimeSeries(range: TimeRange, seed: number) {
  let buckets: string[];
  let count: number;
  if (range === 'today') {
    buckets = Array.from({
      length: 24
    }, (_, i) => `${i}:00`);
    count = 24;
  } else if (range === 'week') {
    buckets = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    count = 7;
  } else if (range === 'month') {
    buckets = Array.from({
      length: 30
    }, (_, i) => `${i + 1}`);
    count = 30;
  } else if (range === 'quarter') {
    buckets = Array.from({
      length: 12
    }, (_, i) => `W${i + 1}`);
    count = 12;
  } else {
    buckets = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    count = 12;
  }
  const multipliers = {
    today: 1,
    week: 15,
    month: 60,
    quarter: 180,
    year: 700,
    custom: 60
  };
  const mult = multipliers[range];
  return buckets.map((bucket, i) => ({
    bucket,
    revenue: Math.round(220 * mult * (0.5 + rand(i + seed) * 1.4)),
    orders: Math.round(3 * mult * (0.5 + rand(i + seed + 17) * 1.5))
  }));
}
export function ReportPage() {
  const {
    branches
  } = useBranches();
  const {
    customers
  } = useCRM();
  const {
    orders,
    updateOrder
  } = useOrders();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange>('year');
  const [branchId, setBranchId] = useState<string>('all');
  const [compareEnabled, setCompareEnabled] = useState(true);
  const activeBranches = branches.filter(b => b.active);
  // Seed based on selections to keep mock data stable
  const seed = (branchId === 'all' ? 99 : branchId.charCodeAt(1)) + timeRange.length;
  const timeSeries = useMemo(() => generateTimeSeries(timeRange, seed), [timeRange, seed]);
  const totalRevenue = timeSeries.reduce((s, d) => s + d.revenue, 0);
  const totalOrders = timeSeries.reduce((s, d) => s + d.orders, 0);
  const aov = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const reservations = Math.round(totalOrders * 0.15);
  const repeatRate = 42 + rand(seed) * 15;
  const avgTip = 8 + rand(seed + 1) * 7;
  const cancelRate = 1 + rand(seed + 2) * 4;
  const kpis = [{
    id: 'revenue',
    label: 'Revenue',
    value: `ETB ${totalRevenue.toLocaleString()}`,
    delta: 12.4 + rand(seed) * 5,
    icon: DollarSign,
    color: 'text-emerald-600',
    bg: 'bg-emerald-100'
  }, {
    id: 'orders',
    label: 'Orders',
    value: totalOrders.toLocaleString(),
    delta: 8.2 + rand(seed + 1) * 5,
    icon: ShoppingBag,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }, {
    id: 'aov',
    label: 'Avg Order Value',
    value: `ETB ${Math.round(aov).toLocaleString()}`,
    delta: 3.1 + rand(seed + 2) * 2,
    icon: Activity,
    color: 'text-purple-600',
    bg: 'bg-purple-100'
  }, {
    id: 'reservations',
    label: 'Reservations',
    value: reservations.toLocaleString(),
    delta: 15.3 + rand(seed + 3) * 10,
    icon: CalendarCheck,
    color: 'text-orange-600',
    bg: 'bg-orange-100'
  }, {
    id: 'repeat',
    label: 'Repeat Rate',
    value: `${repeatRate.toFixed(1)}%`,
    delta: 2.4 + rand(seed + 4) * 3,
    icon: Repeat,
    color: 'text-indigo-600',
    bg: 'bg-indigo-100'
  }, {
    id: 'tip',
    label: 'Avg Tip',
    value: `${avgTip.toFixed(1)}%`,
    delta: 0.8 + rand(seed + 5) * 2,
    icon: Percent,
    color: 'text-pink-600',
    bg: 'bg-pink-100'
  }, {
    id: 'cancel',
    label: 'Cancellation Rate',
    value: `${cancelRate.toFixed(1)}%`,
    delta: -(1.2 + rand(seed + 6) * 2),
    icon: XCircle,
    color: 'text-rose-600',
    bg: 'bg-rose-100',
    inverse: true // down is good
  }];
  // Branch Comparison Data
  const branchComparison = useMemo(() => {
    return activeBranches.map((b, i) => {
      const bSeed = seed + i * 10;
      const bRev = Math.round(totalRevenue * (0.1 + rand(bSeed) * 0.4));
      const bOrd = Math.round(totalOrders * (0.1 + rand(bSeed + 1) * 0.4));
      return {
        id: b.id,
        name: b.name,
        revenue: bRev,
        orders: bOrd,
        aov: bOrd > 0 ? bRev / bOrd : 0,
        tip: 5 + rand(bSeed + 2) * 10,
        repeat: 30 + rand(bSeed + 3) * 30,
        reservations: Math.round(bOrd * 0.15),
        trend: Array.from({
          length: 10
        }, (_, j) => ({
          val: rand(bSeed + j)
        }))
      };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [activeBranches, totalRevenue, totalOrders, seed]);
  // Order Methods Data
  const methodStats = useMemo(() => {
    const keys = Object.keys(methodConfig) as (keyof typeof methodConfig)[];
    return keys.map((k, i) => {
      const mSeed = seed + i * 5;
      return {
        key: k,
        name: methodConfig[k].label,
        color: methodConfig[k].color.replace('text-', ''),
        bg: methodConfig[k].bg,
        value: Math.round(totalOrders * (0.1 + rand(mSeed) * 0.4)),
        revenue: Math.round(totalRevenue * (0.1 + rand(mSeed) * 0.4))
      };
    });
  }, [totalOrders, totalRevenue, seed]);
  const methodColors: Record<string, string> = {
    'purple-700': '#7e22ce',
    'orange-700': '#c2410c',
    'emerald-700': '#047857',
    'blue-700': '#1d4ed8'
  };
  // CRM Data
  const topCustomers = useMemo(() => {
    return [...customers].map(c => ({
      ...c,
      metrics: getCustomerMetrics(c)
    })).sort((a, b) => b.metrics.lifetimeSpend - a.metrics.lifetimeSpend).slice(0, 5);
  }, [customers]);
  const segmentData = useMemo(() => {
    const counts: Record<string, number> = {};
    customers.forEach(c => {
      c.tags.forEach(t => {
        counts[t] = (counts[t] || 0) + 1;
      });
    });
    return Object.entries(counts).map(([tag, count]) => ({
      name: tagMeta[tag as keyof typeof tagMeta]?.label || tag,
      value: count,
      color: tagMeta[tag as keyof typeof tagMeta]?.color || 'gray'
    }));
  }, [customers]);
  const segmentColors: Record<string, string> = {
    amber: '#f59e0b',
    emerald: '#10b981',
    blue: '#3b82f6',
    gray: '#6b7280',
    pink: '#ec4899'
  };
  const birthdaysNext30 = customers.filter(c => c.tags.includes('birthday-month')).length;
  const marketingOptIn = customers.filter(c => c.marketingOptIn).length;
  const optInRate = customers.length > 0 ? marketingOptIn / customers.length * 100 : 0;
  // Reservations Data
  const partySizeData = [{
    size: '1-2',
    count: Math.round(reservations * 0.4)
  }, {
    size: '3-4',
    count: Math.round(reservations * 0.35)
  }, {
    size: '5-6',
    count: Math.round(reservations * 0.15)
  }, {
    size: '7+',
    count: Math.round(reservations * 0.1)
  }];
  // Commissions Data (Actual from OrdersContext)
  const accruedOrders = orders.filter(o => o.commissionStatus === 'accrued');
  const settledOrders = orders.filter(o => o.commissionStatus === 'settled');
  const accruedAmount = accruedOrders.reduce((sum, o) => sum + (o.commissionAmount || 0), 0);
  const settledAmount = settledOrders.reduce((sum, o) => sum + (o.commissionAmount || 0), 0);
  const handleSettleAccrued = () => {
    if (accruedOrders.length === 0) return;
    if (window.confirm(`Mark ETB ${accruedAmount.toLocaleString()} in accrued commissions as settled?`)) {
      accruedOrders.forEach(o => {
        updateOrder(o.id, {
          commissionStatus: 'settled'
        });
      });
    }
  };
  return <div className="flex h-screen w-full bg-[#f9fafb] overflow-hidden font-sans flex-col md:flex-row" data-id="element-3150">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between bg-[#0f172a] text-white p-4 flex-shrink-0 z-30" data-id="element-3151">
        <button onClick={() => setIsSidebarOpen(true)} className="p-1 hover:bg-white/10 rounded-md transition-colors" data-id="element-3152">
          <MenuIcon size={24} data-id="element-3153" />
        </button>
        <span className="font-bold" data-id="element-3154">Reports</span>
        <div className="w-6" data-id="element-3155" />
      </div>

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(v => !v)} data-id="element-3156" />

      <main className="flex-1 overflow-y-auto" data-id="element-3157">
        <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-8" data-id="element-3158">
          {/* Header Controls */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-3159">
            <div data-id="element-3160">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-3161">
                Reports & Analytics
              </h1>
              <p className="text-sm text-gray-500 mt-1" data-id="element-3162">
                Performance overview across your organization
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3" data-id="element-3163">
              <div className="relative" data-id="element-3164">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3165" />
                <select value={branchId} onChange={e => setBranchId(e.target.value)} className="appearance-none pl-9 pr-8 py-2 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-3166">
                  <option value="all" data-id="element-3167">All Branches</option>
                  {activeBranches.map(b => <option key={b.id} value={b.id} data-id="element-3168">
                      {b.name}
                    </option>)}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3169" />
              </div>

              <div className="relative" data-id="element-3170">
                <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3171" />
                <select value={timeRange} onChange={e => setTimeRange(e.target.value as TimeRange)} className="appearance-none pl-9 pr-8 py-2 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-3172">
                  <option value="today" data-id="element-3173">Today</option>
                  <option value="week" data-id="element-3174">This Week</option>
                  <option value="month" data-id="element-3175">This Month</option>
                  <option value="quarter" data-id="element-3176">This Quarter</option>
                  <option value="year" data-id="element-3177">This Year</option>
                  <option value="custom" data-id="element-3178">Custom Range...</option>
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3179" />
              </div>

              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white cursor-pointer hover:bg-gray-50 transition-colors" data-id="element-3180">
                <input type="checkbox" checked={compareEnabled} onChange={e => setCompareEnabled(e.target.checked)} className="rounded text-orange-500 focus:ring-orange-500 border-gray-300" data-id="element-3181" />
                <span className="text-sm font-semibold text-gray-700" data-id="element-3182">
                  Compare YoY
                </span>
              </label>

              <button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-colors shadow-sm" data-id="element-3183">
                <Download size={14} data-id="element-3184" />
                Export
              </button>
            </div>
          </div>

          {/* KPI Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4" data-id="element-3185">
            {kpis.map((kpi, i) => {
            const Icon = kpi.icon;
            const isPositive = kpi.delta > 0;
            const isGood = kpi.inverse ? !isPositive : isPositive;
            return <motion.div key={kpi.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.05
            }} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex flex-col" data-id="element-3186">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3187">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${kpi.bg} ${kpi.color}`} data-id="element-3188">
                      <Icon size={16} data-id="element-3189" />
                    </div>
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide" data-id="element-3190">
                      {kpi.label}
                    </span>
                  </div>
                  <div className="text-xl font-bold text-gray-900 mb-2 tabular-nums" data-id="element-3191">
                    {kpi.value}
                  </div>
                  {compareEnabled && <div className="mt-auto flex items-center justify-between" data-id="element-3192">
                      <div className={`inline-flex items-center gap-1 text-xs font-bold ${isGood ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-3193">
                        {isPositive ? <TrendingUp size={12} data-id="element-3194" /> : <TrendingDown size={12} data-id="element-3195" />}
                        {Math.abs(kpi.delta).toFixed(1)}%
                      </div>
                      <div className="w-12 h-6" data-id="element-3196">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-3197">
                          <AreaChart data={timeSeries.slice(-10)} data-id="element-3198">
                            <Area type="monotone" dataKey="revenue" stroke={isGood ? '#10b981' : '#f43f5e'} fill={isGood ? '#d1fae5' : '#ffe4e6'} strokeWidth={1.5} isAnimationActive={false} data-id="element-3199" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>}
                </motion.div>;
          })}
          </div>

          {/* Main Chart & Methods */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3200">
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-id="element-3201">
              <h3 className="text-base font-bold text-gray-900 mb-6" data-id="element-3202">
                Revenue & Orders over time
              </h3>
              <div className="h-72" data-id="element-3203">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3204">
                  <AreaChart data={timeSeries} margin={{
                  top: 10,
                  right: 10,
                  left: -20,
                  bottom: 0
                }} data-id="element-3205">
                    <defs data-id="element-3206">
                      <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1" data-id="element-3207">
                        <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} data-id="element-3208" />
                        <stop offset="95%" stopColor="#f97316" stopOpacity={0} data-id="element-3209" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-3210" />
                    <XAxis dataKey="bucket" axisLine={false} tickLine={false} tick={{
                    fontSize: 12,
                    fill: '#64748b'
                  }} dy={10} data-id="element-3211" />
                    <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                    fontSize: 12,
                    fill: '#64748b'
                  }} tickFormatter={v => `${v / 1000}k`} data-id="element-3212" />
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                    fontSize: 12,
                    fill: '#64748b'
                  }} data-id="element-3213" />
                    <Tooltip contentStyle={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }} labelStyle={{
                    fontWeight: 'bold',
                    color: '#0f172a',
                    marginBottom: '4px'
                  }} data-id="element-3214" />
                    <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue (ETB)" stroke="#f97316" strokeWidth={3} fillOpacity={1} fill="url(#colorRev)" data-id="element-3215" />
                    <Area yAxisId="right" type="monotone" dataKey="orders" name="Orders" stroke="#3b82f6" strokeWidth={2} fill="none" data-id="element-3216" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col" data-id="element-3217">
              <h3 className="text-base font-bold text-gray-900 mb-4" data-id="element-3218">
                Order Methods
              </h3>
              <div className="h-48 mb-4" data-id="element-3219">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3220">
                  <PieChart data-id="element-3221">
                    <Pie data={methodStats} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={2} dataKey="value" data-id="element-3222">
                      {methodStats.map((entry, index) => <Cell key={`cell-${index}`} fill={methodColors[entry.color] || '#f97316'} data-id="element-3223" />)}
                    </Pie>
                    <Tooltip contentStyle={{
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }} data-id="element-3224" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3 flex-1 overflow-y-auto" data-id="element-3225">
                {methodStats.map(m => <div key={m.key} className="flex items-center justify-between text-sm" data-id="element-3226">
                    <div className="flex items-center gap-2" data-id="element-3227">
                      <div className={`w-3 h-3 rounded-full ${m.bg.split(' ')[0]}`} data-id="element-3228" />
                      <span className="font-semibold text-gray-700" data-id="element-3229">
                        {m.name}
                      </span>
                    </div>
                    <div className="text-right" data-id="element-3230">
                      <div className="font-bold text-gray-900" data-id="element-3231">
                        {m.value.toLocaleString()}
                      </div>
                      <div className="text-[10px] text-gray-500" data-id="element-3232">
                        ETB {m.revenue.toLocaleString()}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Branches Comparison Table */}
          {branchId === 'all' && <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3233">
              <div className="px-5 py-4 border-b border-gray-200" data-id="element-3234">
                <h3 className="text-base font-bold text-gray-900" data-id="element-3235">
                  Branches Comparison
                </h3>
              </div>
              <div className="overflow-x-auto" data-id="element-3236">
                <table className="w-full text-sm text-left" data-id="element-3237">
                  <thead className="bg-gray-50 text-xs uppercase font-bold text-gray-500 border-b border-gray-200" data-id="element-3238">
                    <tr data-id="element-3239">
                      <th className="px-5 py-3" data-id="element-3240">Branch</th>
                      <th className="px-5 py-3 text-right" data-id="element-3241">Revenue</th>
                      <th className="px-5 py-3 text-right" data-id="element-3242">Orders</th>
                      <th className="px-5 py-3 text-right" data-id="element-3243">AOV</th>
                      <th className="px-5 py-3 text-right" data-id="element-3244">Tip %</th>
                      <th className="px-5 py-3 text-right" data-id="element-3245">Repeat %</th>
                      <th className="px-5 py-3 text-right" data-id="element-3246">Reservations</th>
                      <th className="px-5 py-3 w-24" data-id="element-3247">Trend</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-3248">
                    {branchComparison.map(b => <tr key={b.id} className="hover:bg-gray-50 transition-colors" data-id="element-3249">
                        <td className="px-5 py-4 font-bold text-gray-900" data-id="element-3250">
                          {b.name}
                        </td>
                        <td className="px-5 py-4 text-right tabular-nums" data-id="element-3251">
                          ETB {b.revenue.toLocaleString()}
                        </td>
                        <td className="px-5 py-4 text-right tabular-nums" data-id="element-3252">
                          {b.orders.toLocaleString()}
                        </td>
                        <td className="px-5 py-4 text-right tabular-nums" data-id="element-3253">
                          ETB {Math.round(b.aov).toLocaleString()}
                        </td>
                        <td className="px-5 py-4 text-right tabular-nums" data-id="element-3254">
                          {b.tip.toFixed(1)}%
                        </td>
                        <td className="px-5 py-4 text-right tabular-nums" data-id="element-3255">
                          {b.repeat.toFixed(1)}%
                        </td>
                        <td className="px-5 py-4 text-right tabular-nums" data-id="element-3256">
                          {b.reservations.toLocaleString()}
                        </td>
                        <td className="px-5 py-4" data-id="element-3257">
                          <div className="w-16 h-6" data-id="element-3258">
                            <ResponsiveContainer width="100%" height="100%" data-id="element-3259">
                              <AreaChart data={b.trend} data-id="element-3260">
                                <Area type="monotone" dataKey="val" stroke="#f97316" fill="#ffedd5" strokeWidth={1.5} isAnimationActive={false} data-id="element-3261" />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t border-gray-200 font-bold text-gray-900" data-id="element-3262">
                    <tr data-id="element-3263">
                      <td className="px-5 py-4" data-id="element-3264">Total / Avg</td>
                      <td className="px-5 py-4 text-right tabular-nums" data-id="element-3265">
                        ETB {totalRevenue.toLocaleString()}
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums" data-id="element-3266">
                        {totalOrders.toLocaleString()}
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums" data-id="element-3267">
                        ETB {Math.round(aov).toLocaleString()}
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums" data-id="element-3268">
                        {avgTip.toFixed(1)}%
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums" data-id="element-3269">
                        {repeatRate.toFixed(1)}%
                      </td>
                      <td className="px-5 py-4 text-right tabular-nums" data-id="element-3270">
                        {reservations.toLocaleString()}
                      </td>
                      <td className="px-5 py-4" data-id="element-3271"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>}

          {/* CRM & Reservations Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3272">
            {/* Customers (CRM) */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-id="element-3273">
              <div className="flex items-center justify-between mb-6" data-id="element-3274">
                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2" data-id="element-3275">
                  <Users size={18} className="text-blue-500" data-id="element-3276" />
                  Customers & CRM
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-3277">
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-100" data-id="element-3278">
                  <div className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-1" data-id="element-3279">
                    New Customers
                  </div>
                  <div className="text-2xl font-bold text-blue-900" data-id="element-3280">
                    {Math.round(totalOrders * 0.2).toLocaleString()}
                  </div>
                  <div className="text-xs text-blue-600 mt-1 flex items-center gap-1" data-id="element-3281">
                    <TrendingUp size={12} data-id="element-3282" /> +14.2% {compareEnabled && 'YoY'}
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-pink-50 border border-pink-100" data-id="element-3283">
                  <div className="text-xs font-bold text-pink-600 uppercase tracking-wide mb-1 flex items-center gap-1" data-id="element-3284">
                    <Cake size={12} data-id="element-3285" /> Upcoming Birthdays
                  </div>
                  <div className="text-2xl font-bold text-pink-900" data-id="element-3286">
                    {birthdaysNext30}
                  </div>
                  <div className="text-xs text-pink-600 mt-1" data-id="element-3287">
                    in the next 30 days
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 mb-6" data-id="element-3288">
                <div className="w-full sm:w-1/2 h-40" data-id="element-3289">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-3290">
                    <PieChart data-id="element-3291">
                      <Pie data={segmentData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={2} dataKey="value" data-id="element-3292">
                        {segmentData.map((entry, index) => <Cell key={`cell-${index}`} fill={segmentColors[entry.color] || '#9ca3af'} data-id="element-3293" />)}
                      </Pie>
                      <Tooltip contentStyle={{
                      borderRadius: '8px',
                      border: 'none',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }} data-id="element-3294" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-full sm:w-1/2 flex flex-col justify-center space-y-2" data-id="element-3295">
                  <div className="text-sm font-bold text-gray-900 mb-1" data-id="element-3296">
                    Segments
                  </div>
                  {segmentData.map(s => <div key={s.name} className="flex items-center justify-between text-xs" data-id="element-3297">
                      <div className="flex items-center gap-1.5" data-id="element-3298">
                        <div className="w-2 h-2 rounded-full" style={{
                      backgroundColor: segmentColors[s.color] || '#9ca3af'
                    }} data-id="element-3299" />
                        <span className="text-gray-600" data-id="element-3300">{s.name}</span>
                      </div>
                      <span className="font-bold text-gray-900" data-id="element-3301">{s.value}</span>
                    </div>)}
                  <div className="mt-3 pt-3 border-t border-gray-100 text-xs" data-id="element-3302">
                    <span className="text-gray-500" data-id="element-3303">Marketing Opt-in: </span>
                    <span className="font-bold text-gray-900" data-id="element-3304">
                      {optInRate.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>

              <div data-id="element-3305">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3" data-id="element-3306">
                  Top Customers (Lifetime)
                </h4>
                <div className="space-y-3" data-id="element-3307">
                  {topCustomers.map((c, i) => <div key={c.id} className="flex items-center justify-between" data-id="element-3308">
                      <div className="flex items-center gap-3" data-id="element-3309">
                        <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-xs font-bold" data-id="element-3310">
                          {c.firstName[0]}
                          {c.lastName[0]}
                        </div>
                        <div data-id="element-3311">
                          <div className="text-sm font-bold text-gray-900 flex items-center gap-1" data-id="element-3312">
                            {c.firstName} {c.lastName}
                            {i === 0 && <Crown size={12} className="text-amber-500" data-id="element-3313" />}
                          </div>
                          <div className="text-[10px] text-gray-500" data-id="element-3314">
                            {c.metrics.totalOrders} orders
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-bold text-gray-900 tabular-nums" data-id="element-3315">
                        ETB{' '}
                        {Math.round(c.metrics.lifetimeSpend).toLocaleString()}
                      </div>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Reservations */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col" data-id="element-3316">
              <div className="flex items-center justify-between mb-6" data-id="element-3317">
                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2" data-id="element-3318">
                  <CalendarCheck size={18} className="text-orange-500" data-id="element-3319" />
                  Reservations
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-3320">
                <div className="p-4 rounded-xl border border-gray-100 bg-gray-50" data-id="element-3321">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1" data-id="element-3322">
                    Total Bookings
                  </div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-3323">
                    {reservations.toLocaleString()}
                  </div>
                </div>
                <div className="p-4 rounded-xl border border-gray-100 bg-gray-50" data-id="element-3324">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1" data-id="element-3325">
                    No-show Rate
                  </div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-3326">
                    {(4 + rand(seed) * 3).toFixed(1)}%
                  </div>
                </div>
              </div>

              <div className="mb-6" data-id="element-3327">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3" data-id="element-3328">
                  Party Size Distribution
                </h4>
                <div className="h-40" data-id="element-3329">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-3330">
                    <BarChart data={partySizeData} margin={{
                    top: 0,
                    right: 0,
                    left: -20,
                    bottom: 0
                  }} data-id="element-3331">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-3332" />
                      <XAxis dataKey="size" axisLine={false} tickLine={false} tick={{
                      fontSize: 12,
                      fill: '#64748b'
                    }} dy={10} data-id="element-3333" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                      fontSize: 12,
                      fill: '#64748b'
                    }} data-id="element-3334" />
                      <Tooltip cursor={{
                      fill: '#f8fafc'
                    }} contentStyle={{
                      borderRadius: '8px',
                      border: 'none',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }} data-id="element-3335" />
                      <Bar dataKey="count" fill="#f97316" radius={[4, 4, 0, 0]} data-id="element-3336" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="flex-1" data-id="element-3337">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3" data-id="element-3338">
                  Peak Times (Heatmap)
                </h4>
                <div className="grid grid-cols-7 gap-1 h-32" data-id="element-3339">
                  {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => <div key={i} className="flex flex-col gap-1 h-full" data-id="element-3340">
                      <div className="text-[10px] font-bold text-gray-400 text-center mb-1" data-id="element-3341">
                        {day}
                      </div>
                      {[0, 1, 2, 3, 4].map(slot => {
                    const intensity = rand(seed + i * 10 + slot);
                    const opacity = 0.1 + intensity * 0.9;
                    return <div key={slot} className="flex-1 rounded-sm bg-orange-500" style={{
                      opacity
                    }} title={`${day} slot ${slot}`} data-id="element-3342" />;
                  })}
                    </div>)}
                </div>
                <div className="flex items-center justify-between mt-2 text-[10px] text-gray-400 font-semibold" data-id="element-3343">
                  <span data-id="element-3344">Lunch (12pm)</span>
                  <span data-id="element-3345">Dinner (8pm)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Settlement & Commissions */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-id="element-3346">
            <div className="flex items-center justify-between mb-6" data-id="element-3347">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2" data-id="element-3348">
                <Landmark size={18} className="text-indigo-500" data-id="element-3349" />
                Settlement & Commissions
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3350">
              <div className="p-5 rounded-xl bg-indigo-50 border border-indigo-100 flex flex-col justify-between" data-id="element-3351">
                <div data-id="element-3352">
                  <div className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-1" data-id="element-3353">
                    Accrued (Owed)
                  </div>
                  <div className="text-3xl font-bold text-indigo-900 tabular-nums" data-id="element-3354">
                    ETB{' '}
                    {accruedAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                  </div>
                  <div className="text-sm text-indigo-700 mt-1" data-id="element-3355">
                    From {accruedOrders.length} bank-transfer order
                    {accruedOrders.length === 1 ? '' : 's'}
                  </div>
                </div>
                <button onClick={handleSettleAccrued} disabled={accruedOrders.length === 0} className="mt-6 w-full py-2.5 rounded-lg bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 transition-colors disabled:bg-indigo-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm" data-id="element-3356">
                  <CheckCircle2 size={16} data-id="element-3357" />
                  Mark Accrued as Settled
                </button>
              </div>

              <div className="p-5 rounded-xl bg-emerald-50 border border-emerald-100 flex flex-col justify-between" data-id="element-3358">
                <div data-id="element-3359">
                  <div className="text-xs font-bold text-emerald-600 uppercase tracking-wide mb-1" data-id="element-3360">
                    Settled (Collected)
                  </div>
                  <div className="text-3xl font-bold text-emerald-900 tabular-nums" data-id="element-3361">
                    ETB{' '}
                    {settledAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                  </div>
                  <div className="text-sm text-emerald-700 mt-1" data-id="element-3362">
                    From {settledOrders.length} online & settled order
                    {settledOrders.length === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="mt-6 text-xs text-emerald-600/80 font-medium" data-id="element-3363">
                  Online rails (Telebirr, Card, CBE Birr) auto-settle their
                  commissions. Bank transfers are settled manually here.
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>;
}